/* Zeoprix 0.9 page motion: inertial scroll, line and media reveals, the growing video window,
   the top-view catch diagram, the video player, the menu overlay and the color pickers.
   Loaded deferred on every page after gsap, ScrollTrigger, SplitText and Lenis; each feature
   runs only when its elements are on the page. */
(() => {
	const $ = (s, r = document) => r.querySelector(s);
	const $$ = (s, r = document) => [...r.querySelectorAll(s)];
	// the theme editor re-renders sections in place: no smooth scroll and no entrance motion there
	const editor = !!(window.Shopify && window.Shopify.designMode);
	const reduce =
		editor || matchMedia("(prefers-reduced-motion: reduce)").matches;
	const fine = matchMedia("(pointer: fine)").matches;
	const mobile = matchMedia("(max-width: 860px)").matches;
	gsap.registerPlugin(ScrollTrigger, SplitText);
	window.__zx = { heroProgress: 0 };
	// on a slow phone this script runs seconds after the page has painted; an entrance for text the
	// visitor is already reading would hide it and play it in again, so on-screen entrances are skipped
	const fcp = performance.getEntriesByName("first-contentful-paint")[0];
	const late = !!fcp && performance.now() - fcp.startTime > 1000;
	// lines are measured once, in the brand font (it is preloaded, so this is rarely a wait)
	const fontsReady = Promise.race([
		document.fonts.ready,
		new Promise((r) => setTimeout(r, 2500)),
	]);

	// smooth, inertial scrolling (native scroll underneath, so anchors and sticky keep working)
	let lenis = null;
	if (!reduce) {
		lenis = new Lenis({ lerp: 0.085, wheelMultiplier: 0.9 });
		lenis.on("scroll", ScrollTrigger.update);
		gsap.ticker.add((t) => lenis.raf(t * 1000));
		gsap.ticker.lagSmoothing(0);
	}
	// links to a section on this same page glide there instead of jumping
	document.addEventListener("click", (e) => {
		const a = e.target.closest("a[href*='#']");
		if (!a || a.pathname !== location.pathname || a.hash.length < 2) return;
		const t = document.getElementById(a.hash.slice(1));
		if (!t) return;
		e.preventDefault();
		closeMenu();
		if (lenis) lenis.scrollTo(t, { duration: 1.6 });
		else t.scrollIntoView();
	});

	// letter roll on hover: each character has a copy underneath that rolls up. Only screens with a
	// hover pointer get it, split a few labels at a time while the browser is idle (or on first hover)
	const rollSplit = (el) => {
		if (el.dataset.rollReady) return;
		el.dataset.rollReady = "1";
		const s = SplitText.create(el, {
			type: "chars",
			charsClass: "z-ch",
			propIndex: true,
		});
		s.chars.forEach((c) => (c.dataset.ch = c.textContent));
	};
	if (fine) {
		const rolls = $$("[data-roll]");
		rolls.forEach((el) =>
			(el.closest(".z-roll-host") || el).addEventListener(
				"pointerenter",
				() => rollSplit(el),
				{ once: true },
			),
		);
		const idle = (fn) =>
			"requestIdleCallback" in window
				? requestIdleCallback(fn, { timeout: 4000 })
				: setTimeout(fn, 300);
		const next = (deadline) => {
			while (rolls.length && (!deadline || deadline.timeRemaining() > 4))
				rollSplit(rolls.shift());
			if (rolls.length) idle(next);
		};
		fontsReady.then(() => idle(next));
	}

	// a text rises line by line: split when it is about to play, put back together when done
	const lineReveal = (el, vars) =>
		fontsReady.then(() => {
			const split = SplitText.create(el, {
				type: "lines",
				mask: "lines",
				linesClass: "z-ln",
			});
			gsap.set(el, { opacity: 1 });
			return gsap.from(
				split.lines,
				Object.assign(
					{
						yPercent: 118,
						duration: 1.25,
						ease: "expo.out",
						stagger: 0.09,
						onComplete: () => split.revert(),
					},
					vars,
				),
			);
		});

	// scroll reveals, each plays once, from one shared observer: what is below the screen when the page
	// opens is set to its start state and plays as its top passes ~88% of the screen height
	const reveals = new Map();
	const io = new IntersectionObserver(
		(entries) =>
			entries.forEach((e) => {
				const r = reveals.get(e.target);
				if (!r) return;
				if (!r.placed) {
					r.placed = true;
					const b = e.boundingClientRect;
					if (b.top < innerHeight && b.bottom > 0) {
						io.unobserve(e.target);
						reveals.delete(e.target);
						if (!late) r.play();
						return;
					}
					r.hide();
				}
				if (e.isIntersecting) {
					io.unobserve(e.target);
					reveals.delete(e.target);
					r.play();
				}
			}),
		{ rootMargin: "0px 0px -12% 0px" },
	);
	const reveal = (el, hide, play) => {
		reveals.set(el, { hide, play, placed: false });
		io.observe(el);
	};

	const hero = $("#z-hero");
	if (!reduce) {
		if (hero && !late) {
			// intro: the statement and headline rise line by line while the hero window opens
			gsap
				.timeline({ defaults: { ease: "expo.out" } })
				.fromTo(
					hero,
					{ clipPath: "inset(6% 5% 6% 5% round 24px)" },
					{
						clipPath: "inset(0% 0% 0% 0% round 24px)",
						duration: 1.6,
						clearProps: "clipPath",
					},
					0,
				)
				.from(".z-lockup", { y: 14, opacity: 0, duration: 1 }, 0.1)
				.from(
					".z-dock .z-pill",
					{ y: -14, opacity: 0, stagger: 0.08, duration: 1 },
					0.2,
				)
				.from(
					"[data-z-intro-fade]",
					{ y: 22, opacity: 0, stagger: 0.08, duration: 1.2 },
					0.75,
				);
			$$("[data-z-intro-split]").forEach((el, i) =>
				lineReveal(el, { delay: 0.25 + i * 0.2 }),
			);
		}
		if (hero) {
			// the hero copy lifts away as you scroll; the 3D scene reads this progress
			ScrollTrigger.create({
				trigger: hero,
				start: "top top",
				end: "bottom top",
				onUpdate: (s) => (window.__zx.heroProgress = s.progress),
			});
			gsap.to(".z-hero-copy", {
				yPercent: -18,
				opacity: 0.15,
				ease: "none",
				scrollTrigger: {
					trigger: hero,
					start: "top top",
					end: "bottom top",
					scrub: true,
				},
			});
		}

		$$("[data-z-split]").forEach((el) =>
			reveal(
				el,
				() => gsap.set(el, { opacity: 0 }),
				() => lineReveal(el),
			),
		);
		$$("[data-z-fade]").forEach((el) =>
			reveal(
				el,
				() => gsap.set(el, { y: 26, opacity: 0 }),
				() =>
					gsap.fromTo(
						el,
						{ y: 26, opacity: 0 },
						{ y: 0, opacity: 1, duration: 1.2, ease: "expo.out" },
					),
			),
		);
		$$("[data-z-media]").forEach((w) => {
			const inner = w.querySelector("img,video");
			const from = { clipPath: "inset(9% 7% 9% 7% round 24px)" };
			const fromInner = { scale: 1.2, filter: "blur(14px)" };
			reveal(
				w,
				() => {
					gsap.set(w, from);
					if (inner) gsap.set(inner, fromInner);
				},
				() => {
					gsap.fromTo(w, from, {
						clipPath: "inset(0% 0% 0% 0% round 24px)",
						duration: 1.5,
						ease: "expo.out",
						clearProps: "clipPath",
					});
					if (inner)
						gsap.fromTo(inner, fromInner, {
							scale: 1,
							filter: "blur(0px)",
							duration: 1.7,
							ease: "expo.out",
							// keep the parallax transform; elsewhere hand transform back to CSS (hover zoom)
							clearProps: inner.hasAttribute("data-z-speed")
								? "filter"
								: "filter,transform",
						});
				},
			);
		});
		// depth: media drift at their own speed inside their frame
		$$("[data-z-speed]").forEach((el) => {
			const s = parseFloat(el.dataset.zSpeed);
			gsap.fromTo(
				el,
				{ yPercent: -s },
				{
					yPercent: s,
					ease: "none",
					scrollTrigger: {
						trigger: el.closest(".z-win"),
						start: "top bottom",
						end: "bottom top",
						scrub: true,
					},
				},
			);
		});
		// the gold route draws itself once when its section arrives
		const route = $("#z-route");
		if (route) {
			const len = route.getTotalLength();
			gsap.fromTo(
				route,
				{ strokeDasharray: len, strokeDashoffset: len },
				{
					strokeDashoffset: 0,
					duration: 2.4,
					ease: "power2.inOut",
					scrollTrigger: { trigger: ".z-nod", start: "top 55%", once: true },
				},
			);
		}
		// kit callouts: the leader lines draw out from each item, then the labels appear. Phones hide the
		// callouts (z.css), and setting up tweens on hidden elements costs a style pass each, so they skip it
		if ($(".z-callout") && !mobile) {
			gsap.from(".z-callout", {
				scaleX: 0,
				duration: 1.1,
				ease: "expo.out",
				stagger: 0.12,
				delay: 0.5,
				scrollTrigger: { trigger: ".z-lightbox", start: "top 80%", once: true },
			});
			gsap.from(".z-callout .z-label", {
				opacity: 0,
				x: (i, el) => (el.closest(".z-callout--left") ? 10 : -10),
				duration: 0.9,
				ease: "expo.out",
				stagger: 0.12,
				delay: 0.9,
				scrollTrigger: { trigger: ".z-lightbox", start: "top 80%", once: true },
			});
		}
		// reel: the window grows as the section travels up the screen; nothing is pinned. With motion on,
		// the window keeps a full-screen box (.is-clip) and a clip-path opens it: a box that changes size
		// counts as a layout shift and re-lays the page every frame. The video inside is scaled to what
		// covering the open part would look like, times the zoom-out, so every frame looks as before.
		const reelStage = $(".z-reel-stage");
		const reelWin = reelStage && $(".z-reel-win", reelStage);
		if (reelWin) {
			const video = $("video", reelWin);
			const w0 = mobile ? 0.64 : 0.36,
				h0 = mobile ? 0.3 : 0.38,
				aspect = 16 / 9; // the reel clip is 1920x1080
			const reel = { p: 0 };
			let W = reelStage.clientWidth,
				H = reelStage.clientHeight;
			const cover = (w, h) => Math.max(w / aspect, h);
			const draw = () => {
				const p = reel.p,
					w = w0 + (1 - w0) * p,
					h = h0 + (1 - h0) * p;
				reelWin.style.clipPath = `inset(${(((1 - h) * H) / 2).toFixed(1)}px ${(((1 - w) * W) / 2).toFixed(1)}px round ${(24 * (1 - p)).toFixed(1)}px)`;
				if (video)
					video.style.transform = `scale(${((cover(w * W, h * H) / cover(W, H)) * (1.3 - 0.3 * p)).toFixed(4)})`;
			};
			reelStage.classList.add("is-clip");
			draw();
			new ResizeObserver(() => {
				W = reelStage.clientWidth;
				H = reelStage.clientHeight;
				draw();
			}).observe(reelStage);
			gsap
				.timeline({
					scrollTrigger: {
						trigger: ".z-reel",
						start: "top 90%",
						end: "center center",
						scrub: 0.5,
					},
				})
				.to(reel, { p: 1, ease: "power2.inOut", onUpdate: draw }, 0)
				.to(".z-reel-word .z-w1", { xPercent: -35, ease: "power2.inOut" }, 0)
				.to(".z-reel-word .z-w2", { xPercent: 35, ease: "power2.inOut" }, 0)
				.to(".z-reel-word .z-play", { scale: 1.25, ease: "power2.inOut" }, 0)
				.to(
					[".z-reel-word .z-w1", ".z-reel-word .z-w2"],
					{ opacity: 0, ease: "power1.in", duration: 0.45 },
					0.5,
				);
		}
		// tickers run on their own and speed up with scroll velocity
		$$(".z-ticker-track").forEach((track) => {
			const loop = gsap.to(track, {
				xPercent: -50,
				ease: "none",
				duration: 28,
				repeat: -1,
			});
			ScrollTrigger.create({
				onUpdate: (self) => {
					const boost = 1 + Math.min(Math.abs(self.getVelocity()) / 300, 5);
					gsap.to(loop, {
						timeScale: boost,
						duration: 0.25,
						overwrite: true,
						onComplete: () => gsap.to(loop, { timeScale: 1, duration: 1.2 }),
					});
				},
			});
		});
		// magnetic pills
		if (fine)
			$$(".z-pill, .z-minireel").forEach((el) => {
				const xTo = gsap.quickTo(el, "x", {
					duration: 0.6,
					ease: "power3.out",
				});
				const yTo = gsap.quickTo(el, "y", {
					duration: 0.6,
					ease: "power3.out",
				});
				el.addEventListener("pointermove", (e) => {
					const r = el.getBoundingClientRect();
					xTo((e.clientX - r.left - r.width / 2) * 0.22);
					yTo((e.clientY - r.top - r.height / 2) * 0.3);
				});
				el.addEventListener("pointerleave", () => {
					xTo(0);
					yTo(0);
				});
			});
	}

	// looping clips start when they come into view and pause when they leave (no download before);
	// phones get the lighter file
	const clips = $$("video[data-z-clip]");
	if (clips.length && !reduce) {
		const io = new IntersectionObserver(
			(entries) =>
				entries.forEach(({ target: v, isIntersecting }) => {
					if (!isIntersecting) return v.pause();
					if (!v.getAttribute("src"))
						v.src = (mobile && v.dataset.srcSm) || v.dataset.src;
					v.play().catch(() => {});
				}),
			{ rootMargin: "200px 0px" },
		);
		clips.forEach((v) => io.observe(v));
	}

	// catch diagram (top view): the head tips forward and the pillow stops it
	const plot = $(".z-plot");
	if (plot) {
		const head = $("#z-head-line"),
			tip = $("#z-head-tip"),
			cord = $("#z-cord");
		const zones = [0, 1, 2].map((i) => $("#z-zone" + i));
		const steps = $$(".z-step");
		let shape = "wrap",
			tween = null;
		const draw = (p) => {
			const reach = Math.min(1, p / 0.62),
				stop = shape === "wrap" ? 70 : 92,
				y = stop * (1 - Math.pow(1 - reach, 3));
			head.setAttribute(
				"d",
				`M0 0 C 0 ${(y * 0.35).toFixed(1)}, 2.5 ${(y * 0.7).toFixed(1)}, 0 ${y.toFixed(1)}`,
			);
			tip.setAttribute("cy", y.toFixed(1));
			const caught = p >= 0.6;
			if (shape === "wrap") {
				zones[0].setAttribute("d", "M-40 64.6 A76 76 0 0 0 40 64.6");
				zones[1].setAttribute("d", "M-73.3 -20 A76 76 0 0 0 -73.3 20");
				zones[2].setAttribute("d", "M73.3 -20 A76 76 0 0 1 73.3 20");
			} else {
				cord.setAttribute("d", `M-40 88 Q 0 ${caught ? 96 : 118} 40 88`);
				cord.style.strokeDasharray = caught ? "none" : "3 4";
				zones[0].setAttribute("d", cord.getAttribute("d"));
				zones[1].setAttribute("d", "M-96 -26 A99 99 0 0 0 -96 26");
				zones[2].setAttribute("d", "M96 -26 A99 99 0 0 1 96 26");
			}
			zones[0].style.opacity = caught ? 0.85 : 0;
			zones[1].style.opacity = zones[2].style.opacity = p >= 0.8 ? 0.6 : 0;
			steps.forEach((s) =>
				s.classList.toggle("is-on", p >= parseFloat(s.dataset.at) - 0.02),
			);
		};
		// plays by itself once the diagram is on screen; the page keeps scrolling normally
		const play = () => {
			if (reduce) return draw(1);
			if (tween) tween.kill();
			const st = { p: 0 };
			tween = gsap.to(st, {
				p: 1,
				duration: 3.4,
				ease: "none",
				onUpdate: () => draw(st.p),
			});
		};
		draw(reduce ? 1 : 0);
		if (!reduce)
			ScrollTrigger.create({
				trigger: plot,
				start: "top 70%",
				once: true,
				onEnter: play,
			});
		$("#z-catch-replay").addEventListener("click", play);
		const copy = {
			wrap: [
				"The foam meets your chin.",
				"The 360° Wrap closes all the way around, so there is foam waiting under your chin. The Velcro closure sets the fit wherever you press it.",
			],
			classic: [
				"The cord lock closes the gap.",
				"The Classic is a U-shape. Press the cord lock and pull to tighten the front, or turn the pillow around for chin support.",
			],
		};
		$$(".z-seg button").forEach((b) =>
			b.addEventListener("click", () => {
				shape = b.dataset.shape;
				$$(".z-seg button").forEach((x) =>
					x.setAttribute("aria-pressed", x === b),
				);
				gsap.to("#z-pw", {
					opacity: shape === "wrap" ? 1 : 0,
					duration: reduce ? 0 : 0.5,
				});
				gsap.to("#z-pc", {
					opacity: shape === "classic" ? 1 : 0,
					duration: reduce ? 0 : 0.5,
				});
				$("#z-catch-title").textContent = copy[shape][0];
				$("#z-catch-copy").textContent = copy[shape][1];
				play();
			}),
		);
	}

	// product video player: every play control opens the full demo with sound
	const player = $("#z-player");
	const triggers = $$("[data-z-play]");
	let lastTrigger = null;
	const pv = player && $("#z-player-video"),
		pClose = player && $("#z-player-close");
	function openPlayer(trigger) {
		lastTrigger = trigger;
		player.hidden = false;
		if (lenis) lenis.stop();
		if (!reduce) {
			gsap.fromTo(
				player,
				{ opacity: 0 },
				{ opacity: 1, duration: 0.35, ease: "power2.out" },
			);
			gsap.fromTo(
				".z-player-box",
				{ scale: 0.92, y: 24 },
				{ scale: 1, y: 0, duration: 0.9, ease: "expo.out" },
			);
		}
		pv.currentTime = 0;
		pv.play().catch(() => {});
		pClose.focus();
	}
	function closePlayer() {
		if (!player || player.hidden) return false;
		pv.pause();
		gsap.to(player, {
			opacity: 0,
			duration: reduce ? 0 : 0.3,
			ease: "power2.in",
			onComplete: () => {
				player.hidden = true;
				if (lenis) lenis.start();
				if (lastTrigger) lastTrigger.focus();
			},
		});
		return true;
	}
	if (player && triggers.length) {
		triggers.forEach((el) => {
			el.addEventListener("click", (e) => {
				e.stopPropagation();
				openPlayer(el);
			});
			el.addEventListener("keydown", (e) => {
				if (
					(e.key === "Enter" || e.key === " ") &&
					el.getAttribute("role") === "button"
				) {
					e.preventDefault();
					openPlayer(el);
				}
			});
		});
		pClose.addEventListener("click", closePlayer);
		player.addEventListener("click", (e) => {
			if (e.target === player) closePlayer();
		});
	}

	// color pickers: crossfade to the real photo of that color and point the buy button at its listing
	const dockBuy = $("[data-z-dock-buy]"),
		primaryBuy = $("[data-z-buy-primary]");
	const buybar = $("[data-z-buybar]");
	const syncDock = () => {
		if (!primaryBuy) return;
		if (dockBuy) {
			dockBuy.href = primaryBuy.href;
			dockBuy.target = "_blank";
			dockBuy.rel = "noopener sponsored";
		}
		if (buybar) {
			$("a", buybar).href = primaryBuy.href;
			$("[data-z-buybar-label]", buybar).textContent = $(
				"[data-z-buy-label]",
				primaryBuy,
			).textContent;
		}
	};
	syncDock();
	// phones: the bottom buy bar shows once the main buy button has scrolled up out of view
	// (position-based, so a fast fling or a jump past the button still counts)
	if (buybar && primaryBuy) {
		ScrollTrigger.create({
			trigger: primaryBuy,
			start: "bottom top",
			end: "max",
			onToggle: ({ isActive: on }) => {
				buybar.classList.toggle("is-on", on);
				buybar.setAttribute("aria-hidden", on ? "false" : "true");
				$("a", buybar).tabIndex = on ? 0 : -1;
			},
		});
	}
	$$("[data-z-model]").forEach((m) => {
		const img = $("[data-z-model-img]", m),
			buy = $("[data-z-buy]", m),
			label = $("[data-z-buy-label]", m);
		$$(".z-swatch", m).forEach((s) =>
			s.addEventListener("click", () => {
				$$(".z-swatch", m).forEach((x) =>
					x.setAttribute("aria-pressed", x === s),
				);
				buy.href = s.dataset.url;
				label.textContent = `Buy ${s.dataset.color} on Amazon`;
				syncDock();
				m.dispatchEvent(
					new CustomEvent("z:color", { detail: s.dataset.color }),
				);
				if (!img) return;
				const swap = () => {
					img.srcset = s.dataset.srcset;
					img.src = s.dataset.src;
					img.alt = s.dataset.alt;
				};
				if (reduce) return swap();
				gsap.to(img, {
					opacity: 0,
					duration: 0.25,
					onComplete: () => {
						img.onload = () => gsap.to(img, { opacity: 1, duration: 0.45 });
						swap();
					},
				});
			}),
		);
	});

	// menu overlay
	const menu = $("#z-menu"),
		menuBtn = $("#z-menu-btn");
	function openMenu() {
		menu.inert = false;
		menu.classList.add("is-open");
		menuBtn.setAttribute("aria-expanded", "true");
		if (reduce) gsap.set(menu, { clipPath: "inset(0 0 0% 0)" });
		else
			gsap
				.timeline()
				.to(menu, {
					clipPath: "inset(0 0 0% 0)",
					duration: 0.9,
					ease: "expo.inOut",
				})
				.from(
					$$("a", menu),
					{
						yPercent: 60,
						opacity: 0,
						stagger: 0.06,
						duration: 0.9,
						ease: "expo.out",
					},
					"-=.35",
				);
		if (lenis) lenis.stop();
		$("a", menu).focus({ preventScroll: true });
	}
	function closeMenu() {
		if (!menu || !menu.classList.contains("is-open")) return false;
		menuBtn.setAttribute("aria-expanded", "false");
		gsap.to(menu, {
			clipPath: "inset(0 0 100% 0)",
			duration: reduce ? 0 : 0.7,
			ease: "expo.inOut",
			onComplete: () => {
				menu.classList.remove("is-open");
				menu.inert = true;
			},
		});
		if (lenis) lenis.start();
		return true;
	}
	if (menu && menuBtn) {
		menuBtn.addEventListener("click", () => {
			if (!closeMenu()) openMenu();
			else menuBtn.focus();
		});
	}
	addEventListener("keydown", (e) => {
		if (e.key !== "Escape") return;
		if (closePlayer()) return;
		if (closeMenu()) menuBtn.focus();
	});
})();
